'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the Content workspace service.
 * Uses Q-based promises for all operations.
 */

(function ($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new WorkspaceService instance.
   *
   * @param {string} workspaceServiceUrl - The URL of the backend service
   * @param {Object} [serviceSettings] - Settings for the WorkspaceService
   * @param {Object} [serviceSettings.customQ = window.Q] - An implementation of a promise based Q library, i.e. Angular $q.
   * @param {boolean} [serviceSettings.cacheBusting = false] - Indicates whether cache busting strategies should be
   * used when reading content from the server in order to get around browser caching bugs (read: Safari).
   *
   * @typedef {Object} WorkspaceService
   * @property {function} getContent
   * @property {function} createContent
   * @property {function} updateContent
   * @property {function} deleteContent
   *
   * @return {WorkspaceService}
   */
  atex.aceui.service.WorkspaceService = function (workspaceServiceUrl, serviceSettings) {
    var settings = extend({ cacheBusting: false }, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Reads a content from a workspace.
       *
       * @example
       * WorkspaceService.getContent({
       *   alias: myNamespace/article1,
       *   workspace: myWorkspace,
       *   fullTypeInfo: true
       * })
       * .then (function (result) {
       *   ... = result.content.aspects.MyTeaser.headline;
       * })
       *
       * @param {Object} requestArgs
       * @param {Alias} requestArgs.alias -  An alias identifying the content to be retrieved
       * @param {string} requestArgs.workspace -  An id identifying the workspace to retrieve content from
       * @param {AuthToken} requestArgs.authToken - Authentication token as retrieved from the AuthenticationService
       * @param {boolean} [requestArgs.fullTypeInfo] -  Annotates the returned content with full type information
       * @param {number|string} [requestArgs.atLeastRevision] - Get the content with revision number atLeastRevision or later
       *
       * @typedef {Object} ContentResult
       * @property {Content} content - The content as retrieved from the server.
       * @property {string} [revision] - Opaque string representing a certain revision of the content.
       * @returns {Promise.<ContentResult, atex.aceui.error.Error>}
       */
      getContent: function (requestArgs) {
        var deferred = q.defer();

        var formatParam = '?format=json' + (requestArgs.fullTypeInfo ? '+allTypes' : '');
        var variantParam = requestArgs.variant ? '&variant=' + requestArgs.variant : '';
        var url = workspaceServiceUrl + '/' + requestArgs.workspace + '/alias/' + requestArgs.alias + formatParam + variantParam;

        if (settings.cacheBusting === true) {
          url = url + '&cacheBusting=' + Math.random().toString().substring(2);
        }

        if (requestArgs.atLeastRevision) {
          url += `&atLeastRevision=${requestArgs.atLeastRevision}`;
        }

        $.ajax(url, {
          dataType: 'json',

          beforeSend: function (jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader('X-Auth-Token', requestArgs.authToken);
            }
          },

          success: function (data, textStatus, jqXHR) {
            var result = { content: data };
            var eTag = jqXHR.getResponseHeader('ETag');
            if (eTag) {
              result.revision = eTag;
            }
            deferred.resolve(result);
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.WorkspaceService', {
              401: 'Could not authenticate user while getting workspace content "' + requestArgs.alias + '" from the server',
              403: 'Permission denied when getting workspace content "' + requestArgs.alias + '" from the server',
              404: 'Workspace content "' + requestArgs.alias + '" was not found on the server (404)',
              'default': 'There was an error while getting workspace content "' + requestArgs.alias + '" from the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Creates a content draft on a workspace.
       *
       * WorkspaceService.createContent({
       *   content: cleanContent,
       *   workspace: myWorkspace,
       *   fullTypeInfo: true
       * })
       *.then(function(result) {
       *   ... = result.content.aspects.MyTeaser.headline;
       * })
       *
       *
       * @param {Object} requestArgs
       * @param {Alias} requestArgs.alias -  An alias identifying the content to be retrieved
       * @param {string} requestArgs.workspace -  An id identifying the workspace to retrieve content from
       * @param {AuthToken} requestArgs.authToken - Authentication token as retrieved from the AuthenticationService
       * @param {boolean} [requestArgs.fullTypeInfo] -  Annotates the returned content with full type information
       *
       * @typedef {Object} ContentResult
       * @property {Content} content - The content to create on the server.
       * @returns {Promise.<ContentResult, atex.aceui.error.Error>}
       */
      createContent: function (requestArgs) {
        var deferred = q.defer();

        var formatParam = '?format=json' + (requestArgs.fullTypeInfo ? '+allTypes' : '');
        var fullUrl = workspaceServiceUrl + '/' + requestArgs.workspace + formatParam;

        $.ajax(fullUrl, {
          type: 'POST',
          dataType: 'json',
          contentType: 'application/json',
          data: JSON.stringify(requestArgs.content),

          beforeSend: function (jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader('X-Auth-Token', requestArgs.authToken);
            }
          },

          success: function (data) {
            deferred.resolve({ content: data });
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.WorkspaceService', {
              401: 'Could not authenticate user while creating new workspace draft on the server',
              403: 'Permission denied when creating new workspace draft on the server',
              'default': 'There was an error while creating new workspace draft on the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Updates an existing content, you can send the same content back using updateDcontent (although the id, version
       * and meta fields will be ignored). Any aspects which you don't want to update may be omitted.
       *
       * You can also add an list of operations. If only includeing operations, a new version won't be created.
       *
       * Also returns the updated content. Note that the returned content may be different from the content
       * being passed in case any lifecycle hooks were triggered on the server.
       *
       * @example
       * WorkspaceService.updateContent({
       *   alias: 'myNamespace/article1',
       *   origin: revision,
       *   updateMode: 'replace',
       *   fullTypeInfo: true,
       *   workspace: myWorkspace,
       *
       *   content: {
       *     "system": {
       *       "contentType": "doc_example_content"
       *     },
       *     "aspects": {
       *       "doc_example": {
       *         "_type": "doc_example",
       *         "title": "Hello update!"
       *       }
       *     },
       *     "operations": [
       *       {
       *         "_type": "aceAssignToViews",
       *         "views": ["acePublic"]
       *       }
       *     ],
       *   },
       *
       *   authToken: ...
       * })
       * .then(function(result) {
       *   var id = result.content.system.id;
       *   ...
       * })
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Alias} requestArgs.alias - An alias identifying the content to be updated.
       * @param {string} requestArgs.origin - The revision that this update is based on. Enables conflict detection.
       * @param {Content} requestArgs.content - An object containing the data for the content to be updated.
       * @param {AuthToken} requestArgs.authToken - An authentication token.
       * @param {UpdateMode} [requestArgs.updateMode = Update] -
       *        Defines if and how submitted aspects shall be combined with existing aspects.
       * @param {boolean} [requestArgs.fullTypeInfo = false] - Annotates the returned content with type information.
       * @param {string} requestArgs.workspace = An id identifying the workspace to retrieve content from.
       *
       * @typedef {Object} ContentResult - The result of a successfully updated content.
       * @property {Content} content - The content as retrieved from the server.
       * @property {string} revision - Opaque string representing a certain revision of the content.
       *
       * @returns {Promise.<ContentResult, atex.aceui.error.Error>}
       */
      updateContent: function (requestArgs) {
        if (!requestArgs.alias) {
          var e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: 'No alias property present in requestArgs object',
            origin: 'atex.aceui.service.WorkspaceService'
          });

          return q.reject(e);
        }

        var deferred = q.defer();

        if (requestArgs.origin) {
          requestArgs.content.system.origin = requestArgs.origin;
        }

        var formatParam = '?format=json' + (requestArgs.fullTypeInfo ? '+allTypes' : '');
        var updateModeParam = requestArgs.updateMode ? '&updateMode=' + requestArgs.updateMode : '';
        var url = workspaceServiceUrl + '/' + requestArgs.workspace + '/alias/' + requestArgs.alias + formatParam + updateModeParam;

        $.ajax(url, {
          type: 'PUT',
          dataType: 'json',
          contentType: 'application/json',
          data: JSON.stringify(requestArgs.content),

          beforeSend: function (jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader('X-Auth-Token', requestArgs.authToken);
            }
          },

          success: function (data) {
            deferred.resolve({ content: data });
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.WorkspaceService', {
              401: 'Could not authenticate user when updating workspace content "' + requestArgs.alias + '" to the server',
              403: 'Permission denied when storing updating content "' + requestArgs.alias + '" to the server',
              409: 'Conflict occurred while updating workspace content "' + requestArgs.alias + '" to the the server',
              'default': 'There was an error while updating workspace content "' + requestArgs.alias + '" to the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });
        return deferred.promise;
      },

      /**
       * Deletes a content using one of it's aliases.
       *
       * @example
       * WorkspaceService.deleteContent({
       *   workspace: myWorkspace,
       *   alias: myNamespace/article1
       *  }).
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Alias} requestArgs.alias - An alias identifying the content to be deleted.
       * @param {string} requestArgs.workspace = An id identifying the workspace to retrieve content from.
       * @param {AuthToken} requestArgs.authToken - An authentication token.
       *
       * @returns {Promise.<void, atex.aceui.error.Error>}
       */
      deleteContent: function (requestArgs) {
        if (!requestArgs.alias) {
          var e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: 'No alias property present in requestArgs object',
            origin: 'atex.aceui.service.WorkspaceService'
          });

          return q.reject(e);
        }

        var deferred = q.defer();

        var url = workspaceServiceUrl + '/' + requestArgs.workspace + '/alias/' + requestArgs.alias;

        $.ajax(url, {
          type: 'DELETE',
          dataType: 'json',

          beforeSend: function (jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader('X-Auth-Token', requestArgs.authToken);
            }
          },

          success: function () {
            deferred.resolve();
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var msg = 'deleting draft "' + requestArgs.alias + '" on workspace "' + requestArgs.workspace + '".';

            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.WorkspaceService', {
              401: 'Could not authenticate user when ' + msg,
              403: 'Permission denied when ' + msg,
              409: 'Conflict with server version while ' + msg,
              'default': 'There was an error while ' + msg
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Clears a workspace.
       *
       * @example
       * WorkspaceService.clear({
       *   workspace: myWorkspace
       * });
       *
       * @param {Object} requestArgs - Request arguments
       * @param {string} requestArgs.workspace = An id identifying the workspace to retrieve content from.
       * @param {AuthToken} requestArgs.authToken - An authentication token.
       *
       * @returns {Promise.<void, atex.aceui.error.Error>}
       */
      clear: function (requestArgs) {
        var deferred = q.defer();

        var url = workspaceServiceUrl + '/' + requestArgs.workspace;

        $.ajax(url, {
          type: 'DELETE',
          dataType: 'json',

          beforeSend: function (jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader('X-Auth-Token', requestArgs.authToken);
            }
          },

          success: function () {
            deferred.resolve();
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var msg = 'clearing workspace "' + requestArgs.workspace + '".';

            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.WorkspaceService', {
              401: 'Could not authenticate user when ' + msg,
              403: 'Permission denied when ' + msg,
              409: 'Conflict with server version while ' + msg,
              'default': 'There was an error while ' + msg
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Promotes (moves) a content draft from a workspace to the main content repository.
       *
       * @example
       * WorkspaceService.promoteContent({
       *   alias: myNamespace/article1,
       *   workspace: myWorkspace,
       *   fullTypeInfo: true
       * });
       *
       * @param {Object} requestArgs
       * @param {Alias} requestArgs.alias - An alias identifying the content to be updated.
       * @param {string} requestArgs.workspace = An id identifying the workspace to retrieve content from.
       * @param {AuthToken} requestArgs.authToken - An authentication token.
       * @param {boolean} [requestArgs.fullTypeInfo = false] - Annotates the returned content with type information.
       *
       * @typedef {Object} ContentResult - The result of a successfully retrieved content
       * @returns {Promise.<ContentResult, atex.aceui.error.Error>}
       */
      promoteContent: function (requestArgs) {
        var deferred = q.defer();

        var formatParam = '?format=json' + (requestArgs.fullTypeInfo ? '+allTypes' : '');
        var url = workspaceServiceUrl + '/' + requestArgs.workspace + '/alias/' + requestArgs.alias + '/promote' + formatParam;

        var ajaxObj = {
          type: 'PUT',
          dataType: 'json',

          beforeSend: function (jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader('X-Auth-Token', requestArgs.authToken);
            }
          },

          success: function (data, textStatus, jqXHR) {
            var eTag = jqXHR.getResponseHeader('ETag');
            deferred.resolve({ content: data, revision: eTag });
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var msg = 'promoting draft content ' + requestArgs.alias + ' on workspace \'' + requestArgs.workspace + '\'.';

            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.WorkspaceService', {
              401: 'Could not authenticate user while ' + msg,
              403: 'Permission denied while ' + msg,
              409: 'Conflict with server version while ' + msg,
              'default': 'There was an error while ' + msg
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }
            deferred.reject(e);
          }
        };

        if (typeof requestArgs.operations !== 'undefined') {
          ajaxObj.contentType = 'application/json';
          ajaxObj.data = JSON.stringify({ operations: requestArgs.operations });
        }

        $.ajax(url, ajaxObj);

        return deferred.promise;
      }
    };
  };
})(jQuery);
